import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, Divider, Input, Message, Modal, Panel, SelectPicker, Stack, Table, Tooltip, Whisper, toaster } from 'rsuite';

import {
  useCreateActivity,
  useCreateTask,
  useProjectUsers,
  useTaskOrderActualDollarsByTask,
  useTaskOrderTasksSpendingByMonth,
  useProjectActivityForecastSummary,
  useProjects,
  useTaskOrders,
  useTasks,
  useUpdateTask,
} from '../hooks/useQueries';
import { useWbsTemplateItems, useWbsTemplates } from '../hooks/useWbs';
import { formatHours } from '../utils/chartUtils';
import { exportSheetsToXlsx } from '../utils/excelExport';

const { Column, HeaderCell, Cell } = Table;
const rowKey = 'id';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const digitsOnly = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

const formatCurrencyInput = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return currencyFormatter.format(n);
};

const parseMoneyFromDisplay = (raw) => {
  const s = String(raw ?? '').replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const formatPercentFromRatio = (ratio) => {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
};

const ALL_TASKS_PICKER_VALUE = '__all_tasks__';

const TASK_TYPE_OPTIONS = [
  { label: 'Labor', value: 'labor' },
  { label: 'ODC', value: 'odc' },
];

const formatMonthStartLabel = (monthStart) => {
  const s = String(monthStart || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

const getCurrentMonthStartIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const getDeltaVarianceColor = ({ forecastDollars, deltaDollars }) => {
  const forecast = Math.abs(Number(forecastDollars ?? 0));
  const delta = Math.abs(Number(deltaDollars ?? 0));

  if (forecast <= 0) {
    return delta <= 0 ? '#389e0d' : '#cf1322';
  }

  const variance = delta / forecast;
  if (variance > 0.10) return '#cf1322';
  if (variance > 0.05) return '#faad14';
  return '#389e0d';
};

export default function TaskOrderDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const project = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    return (projects || []).find((p) => Number(p?.id) === projectId) ?? null;
  }, [projects, projectId]);

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const taskOrders = useMemo(() => (Array.isArray(taskOrdersData) ? taskOrdersData : []), [taskOrdersData]);
  const selectedTaskOrder = useMemo(() => {
    if (!Number.isInteger(taskOrderId)) return null;
    return (taskOrders || []).find((o) => Number(o?.id) === taskOrderId) ?? null;
  }, [taskOrders, taskOrderId]);

  const createTask = useCreateTask();
  const createTaskFromWbs = useCreateTask();
  const updateTask = useUpdateTask();
  const createActivityFromWbs = useCreateActivity();

  const { data: tasks = [], isLoading: tasksLoading, error: tasksError } = useTasks(
    { projectId, taskOrderId },
    { enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId) }
  );

  const {
    data: projectUsers = [],
    isLoading: projectUsersLoading,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const { data: activityForecastSummary } = useProjectActivityForecastSummary(projectId, null, {
    enabled: Number.isInteger(projectId),
  });

  const { data: actualDollarsSummary } = useTaskOrderActualDollarsByTask(projectId, taskOrderId, {
    enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId),
  });

  const actualDollarsByTaskId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(actualDollarsSummary?.rows) ? actualDollarsSummary.rows : [];
    for (const row of rows) {
      const taskId = Number(row?.taskId);
      if (!Number.isInteger(taskId)) continue;
      const dollars = Number(row?.actualDollars ?? 0);
      map.set(taskId, Number.isFinite(dollars) ? dollars : 0);
    }
    return map;
  }, [actualDollarsSummary?.rows]);

  const forecastHoursByTaskId = useMemo(() => {
    const map = new Map();
    for (const row of activityForecastSummary?.activities ?? []) {
      const taskId = Number(row?.taskId);
      if (!Number.isInteger(taskId)) continue;
      const hours = Number(row?.totalHours ?? 0);
      map.set(taskId, (map.get(taskId) ?? 0) + (Number.isFinite(hours) ? hours : 0));
    }
    return map;
  }, [activityForecastSummary]);

  const tasksWithTotalsRow = useMemo(() => {
    const base = Array.isArray(tasks) ? tasks : [];
    if (base.length === 0) return base;

    let totalForecastHours = 0;
    let totalBudget = 0;
    let totalForecastRevenue = 0;
    let totalActualDollars = 0;

    for (const t of base) {
      const taskId = Number(t?.id);
      if (Number.isInteger(taskId)) {
        totalForecastHours += Number(forecastHoursByTaskId.get(taskId) ?? 0);
      }

      const budget = Number(t?.budgetTarget ?? t?.budget_target ?? 0);
      if (Number.isFinite(budget)) totalBudget += budget;

      const forecastRevenue = Number(t?.projectedRevenue ?? t?.projected_revenue ?? 0);
      if (Number.isFinite(forecastRevenue)) totalForecastRevenue += forecastRevenue;

      if (Number.isInteger(taskId)) {
        const actualDollars = Number(actualDollarsByTaskId.get(taskId) ?? 0);
        if (Number.isFinite(actualDollars)) totalActualDollars += actualDollars;
      }
    }

    return [
      ...base,
      {
        id: -1,
        __totalsRow: true,
        task: 'Totals',
        totalForecastHours,
        budgetTarget: totalBudget,
        projectedRevenue: totalForecastRevenue,
        actualDollars: totalActualDollars,
      },
    ];
  }, [tasks, forecastHoursByTaskId, actualDollarsByTaskId]);

  const taskPickerData = useMemo(
    () => (Array.isArray(tasks) ? tasks : [])
      .map((t) => ({
        label: String(t?.task || `Task ${t?.id}`),
        value: Number(t?.id),
      }))
      .filter((o) => Number.isInteger(o.value)),
    [tasks]
  );

  const allTaskIdsForSpending = useMemo(
    () => taskPickerData
      .map((o) => Number(o.value))
      .filter((id) => Number.isInteger(id)),
    [taskPickerData]
  );

  const spendingTaskPickerData = useMemo(
    () => [{ label: 'All Tasks', value: ALL_TASKS_PICKER_VALUE }, ...taskPickerData],
    [taskPickerData]
  );

  const [taskSpendingOpen, setTaskSpendingOpen] = useState(false);
  const [taskSpendingSelection, setTaskSpendingSelection] = useState(ALL_TASKS_PICKER_VALUE);
  const [excludeCurrentMonthActuals, setExcludeCurrentMonthActuals] = useState(false);
  const [excludeCurrentMonthActualsTable, setExcludeCurrentMonthActualsTable] = useState(false);

  useEffect(() => {
    if (!taskSpendingOpen) return;
    if (taskSpendingSelection === ALL_TASKS_PICKER_VALUE) return;

    const selectedId = Number(taskSpendingSelection);
    const stillExists = taskPickerData.some((o) => Number(o.value) === selectedId);
    if (!stillExists) {
      setTaskSpendingSelection(ALL_TASKS_PICKER_VALUE);
    }
  }, [taskSpendingOpen, taskSpendingSelection, taskPickerData]);

  const taskIdsForSpendingQuery = useMemo(() => {
    if (taskSpendingSelection === ALL_TASKS_PICKER_VALUE) return allTaskIdsForSpending;
    const selectedId = Number(taskSpendingSelection);
    return Number.isInteger(selectedId) ? [selectedId] : [];
  }, [allTaskIdsForSpending, taskSpendingSelection]);

  const taskSpendingSelectionLabel = useMemo(() => {
    if (taskSpendingSelection === ALL_TASKS_PICKER_VALUE) return 'All tasks';
    const selectedId = Number(taskSpendingSelection);
    const found = taskPickerData.find((o) => Number(o.value) === selectedId);
    return String(found?.label || 'Selected task');
  }, [taskPickerData, taskSpendingSelection]);

  const taskSpendingQueries = useTaskOrderTasksSpendingByMonth(projectId, taskOrderId, taskIdsForSpendingQuery, {
    enabled: taskSpendingOpen && taskIdsForSpendingQuery.length > 0,
  });

  const taskSpendingLoading = useMemo(
    () => Array.isArray(taskSpendingQueries) && taskSpendingQueries.some((q) => q.isLoading || q.isFetching),
    [taskSpendingQueries]
  );

  const taskSpendingError = useMemo(() => {
    if (!Array.isArray(taskSpendingQueries)) return null;
    const firstError = taskSpendingQueries.find((q) => q.error);
    return firstError?.error || null;
  }, [taskSpendingQueries]);

  const taskSpendingRows = useMemo(() => {
    if (!Array.isArray(taskSpendingQueries)) return [];
    const byMonth = new Map();

    for (let i = 0; i < taskSpendingQueries.length; i += 1) {
      const query = taskSpendingQueries[i];
      const rows = Array.isArray(query?.data?.rows) ? query.data.rows : [];

      for (const row of rows) {
        const monthStart = String(row?.monthStart || '').slice(0, 10);
        if (!monthStart) continue;

        const entry = byMonth.get(monthStart) || {
          monthStart,
          forecastDollars: 0,
          actualDollars: 0,
        };

        entry.forecastDollars += Number(row?.forecastDollars ?? 0);
        entry.actualDollars += Number(row?.actualDollars ?? 0);
        byMonth.set(monthStart, entry);
      }
    }

    return Array.from(byMonth.values())
      .map((row) => ({
        ...row,
        deltaDollars: Number(row.actualDollars ?? 0) - Number(row.forecastDollars ?? 0),
      }))
      .sort((a, b) => String(a.monthStart).localeCompare(String(b.monthStart)));
  }, [taskSpendingQueries]);

  const currentMonthStartIso = useMemo(() => getCurrentMonthStartIso(), []);

  const taskTableSpendingQueries = useTaskOrderTasksSpendingByMonth(projectId, taskOrderId, allTaskIdsForSpending, {
    enabled:
      Number.isInteger(projectId)
      && Number.isInteger(taskOrderId)
      && excludeCurrentMonthActualsTable
      && allTaskIdsForSpending.length > 0,
  });

  const currentMonthActualByTaskId = useMemo(() => {
    const out = new Map();
    if (!excludeCurrentMonthActualsTable) return out;
    if (!Array.isArray(taskTableSpendingQueries)) return out;

    for (let i = 0; i < taskTableSpendingQueries.length; i += 1) {
      const query = taskTableSpendingQueries[i];
      const taskId = Number(allTaskIdsForSpending[i]);
      if (!Number.isInteger(taskId)) continue;

      const rows = Array.isArray(query?.data?.rows) ? query.data.rows : [];
      let sumCurrentMonthActual = 0;
      for (const row of rows) {
        const monthStart = String(row?.monthStart || '').slice(0, 10);
        if (monthStart !== currentMonthStartIso) continue;
        const actual = Number(row?.actualDollars ?? 0);
        if (Number.isFinite(actual)) sumCurrentMonthActual += actual;
      }

      out.set(taskId, sumCurrentMonthActual);
    }

    return out;
  }, [allTaskIdsForSpending, currentMonthStartIso, excludeCurrentMonthActualsTable, taskTableSpendingQueries]);

  const displayActualDollarsByTaskId = useMemo(() => {
    if (!excludeCurrentMonthActualsTable) return actualDollarsByTaskId;

    const out = new Map();
    for (const [taskId, totalActual] of actualDollarsByTaskId.entries()) {
      const currentMonthActual = Number(currentMonthActualByTaskId.get(taskId) ?? 0);
      out.set(taskId, Number(totalActual ?? 0) - (Number.isFinite(currentMonthActual) ? currentMonthActual : 0));
    }
    return out;
  }, [actualDollarsByTaskId, currentMonthActualByTaskId, excludeCurrentMonthActualsTable]);

  const totalDisplayActualDollars = useMemo(() => {
    const list = Array.isArray(tasks) ? tasks : [];
    return list.reduce((sum, t) => {
      const taskId = Number(t?.id);
      if (!Number.isInteger(taskId)) return sum;
      const actual = Number(displayActualDollarsByTaskId.get(taskId) ?? 0);
      return sum + (Number.isFinite(actual) ? actual : 0);
    }, 0);
  }, [displayActualDollarsByTaskId, tasks]);

  const taskSpendingRowsAdjusted = useMemo(
    () => taskSpendingRows.map((row) => {
      const monthStart = String(row?.monthStart || '').slice(0, 10);
      const isCurrentMonth = monthStart === currentMonthStartIso;
      const rawActualDollars = Number(row?.actualDollars ?? 0);
      const adjustedActualDollars = excludeCurrentMonthActuals && isCurrentMonth ? 0 : rawActualDollars;
      return {
        ...row,
        rawActualDollars,
        adjustedActualDollars,
        deltaDollars: adjustedActualDollars - Number(row?.forecastDollars ?? 0),
        isCurrentMonth,
      };
    }),
    [currentMonthStartIso, excludeCurrentMonthActuals, taskSpendingRows]
  );

  const taskSpendingTotalsAdjusted = useMemo(
    () => taskSpendingRowsAdjusted.reduce(
      (acc, row) => {
        acc.forecastDollars += Number(row?.forecastDollars ?? 0);
        acc.actualDollars += Number(row?.adjustedActualDollars ?? 0);
        acc.deltaDollars += Number(row?.deltaDollars ?? 0);
        return acc;
      },
      { forecastDollars: 0, actualDollars: 0, deltaDollars: 0 }
    ),
    [taskSpendingRowsAdjusted]
  );

  const taskSpendingPastTotalsAdjusted = useMemo(
    () => taskSpendingRowsAdjusted.reduce((acc, row) => {
      const monthStart = String(row?.monthStart || '').slice(0, 10);
      const isPastMonth = monthStart && monthStart < currentMonthStartIso;
      if (!isPastMonth) return acc;
      acc.forecastDollars += Number(row?.forecastDollars ?? 0);
      acc.actualDollars += Number(row?.adjustedActualDollars ?? 0);
      acc.deltaDollars += Number(row?.deltaDollars ?? 0);
      return acc;
    }, {
      forecastDollars: 0,
      actualDollars: 0,
      deltaDollars: 0,
    }),
    [currentMonthStartIso, taskSpendingRowsAdjusted]
  );

  const downloadTaskSpendingCsv = () => {
    if (!Array.isArray(taskSpendingRows) || taskSpendingRows.length === 0) return;

    const escapeCsv = (value) => {
      const s = String(value ?? '');
      if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [
      ['Month', 'Forecast', 'Actual', 'Delta'].join(','),
      ...taskSpendingRows.map((row) => [
        escapeCsv(formatMonthStartLabel(row.monthStart)),
        String(Number(row.forecastDollars ?? 0)),
        (() => {
          const monthStart = String(row?.monthStart || '').slice(0, 10);
          const isCurrentMonth = monthStart === currentMonthStartIso;
          if (excludeCurrentMonthActuals && isCurrentMonth) return '0';
          return String(Number(row.actualDollars ?? 0));
        })(),
        (() => {
          const monthStart = String(row?.monthStart || '').slice(0, 10);
          const isPastMonth = monthStart && monthStart < currentMonthStartIso;
          return isPastMonth ? String(Number(row.deltaDollars ?? 0)) : '';
        })(),
      ].join(',')),
      [
        escapeCsv('Totals'),
        String(Number(taskSpendingTotalsAdjusted.forecastDollars ?? 0)),
        String(Number(taskSpendingTotalsAdjusted.actualDollars ?? 0)),
        String(Number(taskSpendingPastTotalsAdjusted.deltaDollars ?? 0)),
      ].join(','),
    ];

    const csv = `${lines.join('\n')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const selectionSlug =
      taskSpendingSelection === ALL_TASKS_PICKER_VALUE
        ? 'all-tasks'
        : `task-${Number(taskSpendingSelection)}`;
    a.download = `task-order-${projectId}-${taskOrderId}-${selectionSlug}-spend-by-month.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadTaskSpendingExcel = async () => {
    if (!Array.isArray(taskSpendingRowsAdjusted) || taskSpendingRowsAdjusted.length === 0) return;

    const selectionSlug =
      taskSpendingSelection === ALL_TASKS_PICKER_VALUE
        ? 'all-tasks'
        : `task-${Number(taskSpendingSelection)}`;

    const sheetRows = taskSpendingRowsAdjusted.map((row) => {
      const monthStart = String(row?.monthStart || '').slice(0, 10);
      const isPastMonth = monthStart && monthStart < currentMonthStartIso;

      return {
        month: formatMonthStartLabel(monthStart),
        forecast: Number(row?.forecastDollars ?? 0),
        actual: Number(row?.adjustedActualDollars ?? 0),
        delta: isPastMonth ? Number(row?.deltaDollars ?? 0) : null,
      };
    });

    sheetRows.push({
      month: 'Totals',
      forecast: Number(taskSpendingTotalsAdjusted.forecastDollars ?? 0),
      actual: Number(taskSpendingTotalsAdjusted.actualDollars ?? 0),
      delta: Number(taskSpendingPastTotalsAdjusted.deltaDollars ?? 0),
    });

    try {
      await exportSheetsToXlsx({
        fileName: `task-order-${projectId}-${taskOrderId}-${selectionSlug}-spend-by-month.xlsx`,
        sheets: [
          {
            name: 'Spend by Month',
            columns: [
              { header: 'Month', key: 'month', width: 18 },
              { header: 'Forecast', key: 'forecast', width: 16, numFmt: '$#,##0;[Red]-$#,##0' },
              { header: 'Actual', key: 'actual', width: 16, numFmt: '$#,##0;[Red]-$#,##0' },
              { header: 'Delta', key: 'delta', width: 16, numFmt: '$#,##0;[Red]-$#,##0' },
            ],
            rows: sheetRows,
          },
        ],
      });
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || err || 'Failed to download Excel.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskBudgetTarget, setNewTaskBudgetTarget] = useState('');
  const [newTaskType, setNewTaskType] = useState('labor');
  const [newTaskLeadUserId, setNewTaskLeadUserId] = useState(null);

  const projectMemberOptions = useMemo(
    () => (Array.isArray(projectUsers) ? projectUsers : [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value)),
    [projectUsers]
  );

  const [addTaskFromWbsOpen, setAddTaskFromWbsOpen] = useState(false);
  const [addTaskFromWbsPending, setAddTaskFromWbsPending] = useState(false);
  const [wbsTemplateId, setWbsTemplateId] = useState(null);
  const [wbsTaskName, setWbsTaskName] = useState('');
  const [wbsSelectedItemIds, setWbsSelectedItemIds] = useState([]);

  const { data: wbsTemplates = [], isLoading: wbsTemplatesLoading, error: wbsTemplatesError } = useWbsTemplates({
    enabled: addTaskFromWbsOpen,
  });

  const { data: wbsItems = [], isLoading: wbsItemsLoading, error: wbsItemsError } = useWbsTemplateItems(wbsTemplateId, {
    enabled: addTaskFromWbsOpen && Number.isInteger(Number(wbsTemplateId)),
  });

  useEffect(() => {
    if (!addTaskFromWbsOpen) return;
    if (Number.isInteger(Number(wbsTemplateId))) return;
    const firstTemplateId = Number(wbsTemplates?.[0]?.id);
    if (!Number.isInteger(firstTemplateId)) return;
    setWbsTemplateId(firstTemplateId);
    if (!String(wbsTaskName || '').trim()) {
      const firstName = String(wbsTemplates?.[0]?.name ?? '').trim();
      if (firstName) setWbsTaskName(firstName);
    }
  }, [addTaskFromWbsOpen, wbsTemplateId, wbsTemplates, wbsTaskName]);

  const sortedWbsItems = useMemo(() => {
    return (Array.isArray(wbsItems) ? wbsItems : [])
      .map((i) => ({
        id: Number(i?.id),
        title: String(i?.title ?? '').trim(),
        assumptions: String(i?.assumptions ?? '').trim(),
        sortOrder: Number(i?.sortOrder ?? 0),
      }))
      .filter((i) => Number.isInteger(i.id) && i.title)
      .sort((a, b) => {
        const so = a.sortOrder - b.sortOrder;
        if (so !== 0) return so;
        return a.id - b.id;
      });
  }, [wbsItems]);

  useEffect(() => {
    if (!addTaskFromWbsOpen) return;
    const ids = sortedWbsItems.map((i) => i.id);
    setWbsSelectedItemIds(ids);
  }, [addTaskFromWbsOpen, wbsTemplateId, sortedWbsItems]);

  // Inline editing state (copied from old Home page behavior)
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState('');
  const [editingBudgetTaskId, setEditingBudgetTaskId] = useState(null);
  const [editingBudgetValue, setEditingBudgetValue] = useState('');
  const [editingBudgetOriginal, setEditingBudgetOriginal] = useState(undefined);
  const editTaskNameInputRef = useRef(null);
  const editBudgetInputRef = useRef(null);
  const skipTaskNameBlurCommitRef = useRef(false);
  const skipBudgetBlurCommitRef = useRef(false);
  const committingTaskNameRef = useRef(false);
  const committingBudgetRef = useRef(false);

  useEffect(() => {
    if (!Number.isInteger(Number(editingTaskId))) return;
    queueMicrotask(() => {
      editTaskNameInputRef.current?.focus?.();
    });
  }, [editingTaskId]);

  useEffect(() => {
    if (!Number.isInteger(Number(editingBudgetTaskId))) return;
    queueMicrotask(() => {
      editBudgetInputRef.current?.focus?.();
    });
  }, [editingBudgetTaskId]);

  const beginEditTaskName = (rowData) => {
    const id = Number(rowData?.id);
    if (!Number.isInteger(id)) return;
    setEditingTaskId(id);
    setEditingTaskName(String(rowData?.task ?? ''));
    setEditingBudgetTaskId(null);
  };

  const cancelEditTaskName = () => {
    setEditingTaskId(null);
    setEditingTaskName('');
  };

  const commitEditTaskName = async () => {
    if (committingTaskNameRef.current) return;
    const taskId = Number(editingTaskId);
    if (!Number.isInteger(taskId)) return;

    const originalTask = String(editingTaskName || '').trim();
    const nextTask = String(editTaskNameInputRef.current?.value ?? editingTaskName ?? '').trim();
    if (!nextTask) {
      cancelEditTaskName();
      return;
    }

    if (nextTask === originalTask) {
      cancelEditTaskName();
      return;
    }

    committingTaskNameRef.current = true;
    try {
      await updateTask.mutateAsync({ taskId, projectId, payload: { task: nextTask } });
      toaster.push(
        <Message showIcon type="success" closable>
          Task name updated.
        </Message>,
        { placement: 'topEnd' }
      );
      cancelEditTaskName();
    } finally {
      committingTaskNameRef.current = false;
    }
  };

  const beginEditBudget = (rowData) => {
    const id = Number(rowData?.id);
    if (!Number.isInteger(id)) return;
    setEditingBudgetTaskId(id);
    const v = rowData?.budgetTarget ?? rowData?.budget_target;
    const original = v === null || v === undefined ? null : Number(v);
    setEditingBudgetOriginal(original);
    setEditingBudgetValue(v === null || v === undefined ? '' : String(v));
    setEditingTaskId(null);
  };

  const cancelEditBudget = () => {
    setEditingBudgetTaskId(null);
    setEditingBudgetValue('');
    setEditingBudgetOriginal(undefined);
  };

  const commitEditBudget = async () => {
    if (committingBudgetRef.current) return;
    const taskId = Number(editingBudgetTaskId);
    if (!Number.isInteger(taskId)) return;

    const trimmed = String(editBudgetInputRef.current?.value ?? editingBudgetValue ?? '').trim();
    const parsed = trimmed === '' ? null : parseMoneyFromDisplay(trimmed);
    const nextBudgetTarget = parsed;
    if (nextBudgetTarget !== null && !Number.isFinite(nextBudgetTarget)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Budget must be a number.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (Number.isFinite(nextBudgetTarget) && nextBudgetTarget < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Budget cannot be negative.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const original = editingBudgetOriginal;
    const unchanged =
      (original === null && nextBudgetTarget === null) ||
      (Number.isFinite(original) && Number.isFinite(nextBudgetTarget) && original === nextBudgetTarget);

    if (unchanged) {
      cancelEditBudget();
      return;
    }

    committingBudgetRef.current = true;
    try {
      await updateTask.mutateAsync({ taskId, projectId, payload: { budgetTarget: nextBudgetTarget } });
      toaster.push(
        <Message showIcon type="success" closable>
          Allocated budget updated.
        </Message>,
        { placement: 'topEnd' }
      );
      cancelEditBudget();
    } finally {
      committingBudgetRef.current = false;
    }
  };

  const goToTask = (rowData) => {
    const taskId = Number(rowData?.id);
    if (!Number.isInteger(taskId)) return;
    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
      navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}`);
      return;
    }
    navigate(`/tasks/${taskId}`);
  };

  const submitAddTask = async () => {
    const task = String(newTaskName || '').trim();
    if (!task) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please enter a task name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const budgetDigits = digitsOnly(newTaskBudgetTarget);
    const normalizedTaskType = String(newTaskType || '').toLowerCase();
    if (normalizedTaskType !== 'labor' && normalizedTaskType !== 'odc') {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a valid task type.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const payload = { task, projectId, taskOrderId, taskType: normalizedTaskType };

    if (!Number.isInteger(newTaskLeadUserId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a task lead.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    payload.leadUserId = Number(newTaskLeadUserId);

    if (budgetDigits) {
      const n = Number(budgetDigits);
      if (!Number.isFinite(n)) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Budget must be a number.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
      if (n < 0) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Budget cannot be negative.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
      payload.budgetTarget = n;
    }

    await createTask.mutateAsync(payload);
    setAddTaskOpen(false);
    setNewTaskName('');
    setNewTaskBudgetTarget('');
    setNewTaskType('labor');
    setNewTaskLeadUserId(null);
  };

  const openAddTaskFromWbs = () => {
    setWbsTemplateId(null);
    setWbsTaskName('');
    setWbsSelectedItemIds([]);
    setAddTaskFromWbsOpen(true);
  };

  const submitAddTaskFromWbs = async () => {
    const templateId = Number(wbsTemplateId);
    if (!Number.isInteger(templateId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a WBS template.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const task = String(wbsTaskName || '').trim();
    if (!task) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please enter a task name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (sortedWbsItems.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Selected template has no items.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const selectedSet = new Set((Array.isArray(wbsSelectedItemIds) ? wbsSelectedItemIds : []).map((v) => Number(v)));
    const selectedItems = sortedWbsItems.filter((i) => selectedSet.has(i.id));
    if (selectedItems.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select at least one activity to import.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    setAddTaskFromWbsPending(true);
    try {
      const createdTask = await createTaskFromWbs.mutateAsync({ task, projectId, taskOrderId });
      const createdTaskId = Number(createdTask?.id);
      if (!Number.isInteger(createdTaskId)) {
        throw new Error('Failed to create task.');
      }

      for (const item of selectedItems) {
        // eslint-disable-next-line no-await-in-loop
        await createActivityFromWbs.mutateAsync({
          taskId: createdTaskId,
          payload: { name: item.title, assumptions: item.assumptions, leadUserId: null, skipLeadAutoAssign: true },
        });
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Created task and imported {selectedItems.length} activities.
        </Message>,
        { placement: 'topEnd' }
      );

      setAddTaskFromWbsOpen(false);
      setWbsTemplateId(null);
      setWbsTaskName('');
      setWbsSelectedItemIds([]);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Create from WBS failed.')}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setAddTaskFromWbsPending(false);
    }
  };

  if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project/task order.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>Task Order</h3>
        <Stack spacing={10} wrap>
          <Button appearance="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to project
            </span>
          </Button>
          <Button
            appearance="ghost"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Actuals
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </span>
          </Button>
          <Button
            appearance="ghost"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/invoices`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Invoices
              <i className="bi bi-receipt" aria-hidden="true" />
            </span>
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}
      {taskOrdersError ? <Message type="error" showIcon>Failed to load task orders.</Message> : null}

      <Panel bordered header={<span>Summary</span>}>
        {projectsLoading ? <div>Loading project…</div> : null}
        {taskOrdersLoading ? <div>Loading task order…</div> : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Project</div>
            <div style={{ fontWeight: 800 }}>{project?.name || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Task order</div>
            <div style={{ fontWeight: 800 }}>{selectedTaskOrder?.name || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Dates</div>
            <div style={{ fontWeight: 700 }}>
              {selectedTaskOrder?.startDate && selectedTaskOrder?.endDate
                ? `${selectedTaskOrder.startDate} → ${selectedTaskOrder.endDate}`
                : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
            <div style={{ fontWeight: 800 }}>
              {selectedTaskOrder?.budgetTarget === null || selectedTaskOrder?.budgetTarget === undefined
                ? '—'
                : formatCurrency(selectedTaskOrder.budgetTarget)}
            </div>
          </div>
        </div>

        {selectedTaskOrder ? null : (
          <div style={{ marginTop: 10 }}>
            <Message type="warning" showIcon>
              Task order not found for this project.
            </Message>
          </div>
        )}
      </Panel>

      <Panel
        bordered
        header={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>
              Tasks{selectedTaskOrder?.name ? ` — ${selectedTaskOrder.name}` : ''}
            </span>
            <Stack spacing={8}>
              <Checkbox
                checked={excludeCurrentMonthActualsTable}
                onChange={(_value, checked) => setExcludeCurrentMonthActualsTable(Boolean(checked))}
              >
                Exclude current month actuals
              </Checkbox>

              <Button
                size="xs"
                appearance="ghost"
                onClick={() => setTaskSpendingOpen(true)}
                disabled={tasksLoading || taskPickerData.length === 0}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Task Spend by Month
                  <i className="bi bi-graph-up" aria-hidden="true" />
                </span>
              </Button>
              <Button
                size="xs"
                appearance="ghost"
                onClick={openAddTaskFromWbs}
                disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Add from WBS
                  <i className="bi bi-diagram-3" aria-hidden="true" />
                </span>
              </Button>
              <Button size="xs" appearance="ghost" onClick={() => setAddTaskOpen(true)} disabled={createTask.isPending}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Add Task
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
              </Button>
            </Stack>
          </div>
        }
        style={{ marginTop: 12 }}
      >
        {tasksLoading ? <div>Loading tasks…</div> : null}
        {tasksError ? <Message type="error" showIcon>Failed to load tasks.</Message> : null}

        <Table
          className="ppc-table--wrap-no-break ppc-table--cursor-default"
          shouldUpdateScroll={false}
          autoHeight
          minHeight={260}
          rowHeight={62}
          headerHeight={44}
          data={tasksWithTotalsRow}
          bordered
          cellBordered
          rowKey={rowKey}
          onRowClick={(rowData) => {
            if (rowData?.__totalsRow) return;
            if (Number.isInteger(Number(editingTaskId))) return;
            if (Number.isInteger(Number(editingBudgetTaskId))) return;
            goToTask(rowData);
          }}
        >
          <Column flexGrow={3} minWidth={220} align="center">
            <HeaderCell>Task</HeaderCell>
            <Cell>
              {(rowData) => {
                if (rowData?.__totalsRow) {
                  return <span style={{ fontWeight: 600 }}>Totals</span>;
                }
                const id = Number(rowData?.id);
                const isEditing = Number.isInteger(id) && id === Number(editingTaskId);
                if (!isEditing) {
                  return (
                    <div
                      className="ppc-editable-cell"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditTaskName(rowData);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          beginEditTaskName(rowData);
                        }
                      }}
                      title="Click to edit"
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          width: '100%',
                          minWidth: 0,
                          padding: '0 16px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: '16px',
                          textAlign: 'center',
                        }}
                      >
                        {rowData?.task}
                      </span>
                      <i
                        className="bi bi-pencil ppc-editable-cell__icon"
                        aria-hidden="true"
                        style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      ref={editTaskNameInputRef}
                      type="text"
                      defaultValue={editingTaskName}
                      onBlur={() => {
                        if (skipTaskNameBlurCommitRef.current) {
                          skipTaskNameBlurCommitRef.current = false;
                          return;
                        }
                        void commitEditTaskName();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          void commitEditTaskName();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          skipTaskNameBlurCommitRef.current = true;
                          cancelEditTaskName();
                        }
                      }}
                      disabled={updateTask.isPending}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        height: 30,
                        padding: '4px 8px',
                        outline: 'none',
                      }}
                    />
                  </div>
                );
              }}
            </Cell>
          </Column>

          <Column width={96}>
            <HeaderCell>Activities</HeaderCell>
            <Cell>
              {(rowData) => {
                if (rowData?.__totalsRow) return '—';
                const count = Array.isArray(rowData?.activities) ? rowData.activities.length : 0;
                return String(count);
              }}
            </Cell>
          </Column>

          <Column width={160} align="right">
            <HeaderCell>Total forecasted hours</HeaderCell>
            <Cell>
              {(rowData) => {
                if (rowData?.__totalsRow) return <span style={{ fontWeight: 600 }}>{formatHours(rowData?.totalForecastHours ?? 0)}</span>;
                const taskId = Number(rowData?.id);
                const hours = Number.isInteger(taskId) ? (forecastHoursByTaskId.get(taskId) ?? 0) : 0;
                return formatHours(hours);
              }}
            </Cell>
          </Column>

          <Column width={130} align="right">
            <HeaderCell>Allocated budget</HeaderCell>
            <Cell>
              {(rowData) => {
                if (rowData?.__totalsRow) {
                  const v = rowData?.budgetTarget ?? rowData?.budget_target;
                  return <span style={{ fontWeight: 600 }}>{formatCurrency(v)}</span>;
                }
                const id = Number(rowData?.id);
                const isEditing = Number.isInteger(id) && id === Number(editingBudgetTaskId);

                if (!isEditing) {
                  const v = rowData?.budgetTarget ?? rowData?.budget_target;
                  return (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditBudget(rowData);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          beginEditBudget(rowData);
                        }
                      }}
                      title="Click to edit"
                      style={{ width: '100%', textAlign: 'right', minWidth: 0 }}
                    >
                      <span style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v === null || v === undefined ? '—' : formatCurrency(v)}
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      ref={editBudgetInputRef}
                      type="text"
                      defaultValue={editingBudgetValue}
                      onBlur={() => {
                        if (skipBudgetBlurCommitRef.current) {
                          skipBudgetBlurCommitRef.current = false;
                          return;
                        }
                        void commitEditBudget();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          void commitEditBudget();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          skipBudgetBlurCommitRef.current = true;
                          cancelEditBudget();
                        }
                      }}
                      disabled={updateTask.isPending}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        textAlign: 'right',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        height: 30,
                        padding: '4px 8px',
                        outline: 'none',
                      }}
                    />
                  </div>
                );
              }}
            </Cell>
          </Column>

          <Column width={130} align="right">
            <HeaderCell>Forecast</HeaderCell>
            <Cell>
              {(rowData) => {
                const forecast = Number(rowData?.projectedRevenue ?? rowData?.projected_revenue ?? 0);
                const budget = Number(rowData?.budgetTarget ?? rowData?.budget_target ?? 0);
                const exceedsBudget = Number.isFinite(forecast)
                  && Number.isFinite(budget)
                  && budget > 0
                  && forecast > budget;

                return (
                  <div
                    style={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        right: exceedsBudget ? 16 : 0,
                        top: '50%',
                        transform: `translateY(-14px)`,
                        color: exceedsBudget ? '#cf1322' : undefined,
                        fontWeight: rowData?.__totalsRow ? 600 : undefined,
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                      }}
                    >
                      {formatCurrency(forecast)}
                    </span>

                    {exceedsBudget ? (
                      <span
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-14px)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 12,
                          width: 12,
                        }}
                      >
                        <Whisper
                          trigger="hover"
                          placement="top"
                          speaker={(
                            <Tooltip>
                              Forecast exceeds allocated budget.
                            </Tooltip>
                          )}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 12,
                              width: 12,
                              cursor: 'help',
                            }}
                          >
                            <i
                              className="bi bi-exclamation-triangle-fill"
                              style={{ color: '#cf1322', fontSize: 12, lineHeight: 1 }}
                            />
                          </span>
                        </Whisper>
                      </span>
                    ) : null}
                  </div>
                );
              }}
            </Cell>
          </Column>

          <Column width={130} align="right">
            <HeaderCell>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Actual $
                <Whisper
                  trigger="hover"
                  placement="top"
                  speaker={(
                    <Tooltip>
                      Actual $ is not the true invoiced amount, check the invoices tab to see true invoiced amount.
                    </Tooltip>
                  )}
                >
                  <i className="bi bi-info-circle" aria-hidden="true" style={{ fontSize: 12, cursor: 'help' }} />
                </Whisper>
              </span>
            </HeaderCell>
            <Cell>
              {(rowData) => {
                if (rowData?.__totalsRow) {
                  return <span style={{ fontWeight: 600 }}>{formatCurrency(totalDisplayActualDollars)}</span>;
                }
                const taskId = Number(rowData?.id);
                const value = Number.isInteger(taskId) ? Number(displayActualDollarsByTaskId.get(taskId) ?? 0) : 0;
                return formatCurrency(value);
              }}
            </Cell>
          </Column>

          <Column width={110} align="right">
            <HeaderCell>% Budget Used</HeaderCell>
            <Cell>
              {(rowData) => {
                if (rowData?.__totalsRow) {
                  const totalBudget = Number(selectedTaskOrder?.budgetTarget ?? selectedTaskOrder?.budget_target ?? 0);
                  if (!Number.isFinite(totalBudget) || totalBudget <= 0) return '—';
                  const totalActual = Number(totalDisplayActualDollars ?? 0);
                  return <span style={{ fontWeight: 600 }}>{formatPercentFromRatio(totalActual / totalBudget)}</span>;
                }

                const budget = Number(rowData?.budgetTarget ?? rowData?.budget_target ?? 0);
                if (!Number.isFinite(budget) || budget <= 0) return '—';

                const taskId = Number(rowData?.id);
                const actual = Number.isInteger(taskId) ? Number(displayActualDollarsByTaskId.get(taskId) ?? 0) : 0;
                return formatPercentFromRatio(actual / budget);
              }}
            </Cell>
          </Column>

          <Column width={120} fixed="right" align="center">
            <HeaderCell>Open</HeaderCell>
            <Cell>
              {(rowData) => (rowData?.__totalsRow ? null : (
                <Button
                  size="xs"
                  appearance="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToTask(rowData);
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    View
                    <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                  </span>
                </Button>
              ))}
            </Cell>
          </Column>
        </Table>
      </Panel>

      <Modal
        open={taskSpendingOpen}
        onClose={() => setTaskSpendingOpen(false)}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>Task Actual vs Forecast Spend by Month</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Task</div>
              <SelectPicker
                data={spendingTaskPickerData}
                value={taskSpendingSelection}
                onChange={(v) => setTaskSpendingSelection(v ?? ALL_TASKS_PICKER_VALUE)}
                cleanable={false}
                searchable
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ fontSize: 12, color: '#666' }}>
              Showing: {taskSpendingSelectionLabel}. Values are summed by month.
            </div>

            <Checkbox
              checked={excludeCurrentMonthActuals}
              onChange={(_value, checked) => setExcludeCurrentMonthActuals(Boolean(checked))}
            >
              Exclude current month actuals
            </Checkbox>

            {taskSpendingLoading ? <div>Loading monthly spending…</div> : null}

            {taskSpendingError ? (
              <Message type="error" showIcon>
                Failed to load monthly spending.
              </Message>
            ) : null}

            {!taskSpendingLoading && !taskSpendingError && taskSpendingRows.length === 0 ? (
              <Message type="info" showIcon>
                No monthly spending found for this task order.
              </Message>
            ) : null}

            {!taskSpendingLoading && !taskSpendingError && taskSpendingRows.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Month</th>
                      <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Forecast</th>
                      <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Actual</th>
                      <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskSpendingRowsAdjusted.map((row) => {
                      const monthStart = String(row?.monthStart || '');
                      const forecast = Number(row?.forecastDollars ?? 0);
                      const actual = Number(row?.adjustedActualDollars ?? 0);
                      const rawActual = Number(row?.rawActualDollars ?? 0);
                      const delta = Number(row?.deltaDollars ?? 0);
                      const isPastMonth = monthStart.slice(0, 10) < currentMonthStartIso;
                      const deltaColor = getDeltaVarianceColor({
                        forecastDollars: forecast,
                        deltaDollars: delta,
                      });
                      const isExcludedCurrentMonth = excludeCurrentMonthActuals && row?.isCurrentMonth;

                      return (
                        <tr key={monthStart}>
                          <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0' }}>
                            {formatMonthStartLabel(monthStart)}
                          </td>
                          <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                            {formatCurrency(forecast)}
                          </td>
                          <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                            {isExcludedCurrentMonth ? (
                              <span
                                style={{
                                  textDecoration: 'line-through',
                                  textDecorationThickness: '2px',
                                  textDecorationColor: '#cf1322',
                                  color: '#999',
                                }}
                                title="Excluded from totals"
                              >
                                {formatCurrency(rawActual)}
                              </span>
                            ) : (
                              formatCurrency(actual)
                            )}
                          </td>
                          <td
                            style={{
                              padding: '8px 6px',
                              borderBottom: '1px solid #f0f0f0',
                              textAlign: 'right',
                              color: isPastMonth ? deltaColor : '#999',
                            }}
                          >
                            {isPastMonth ? formatCurrency(delta) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', fontWeight: 800 }}>Totals</td>
                      <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', textAlign: 'right', fontWeight: 800 }}>
                        {formatCurrency(taskSpendingTotalsAdjusted.forecastDollars ?? 0)}
                      </td>
                      <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', textAlign: 'right', fontWeight: 800 }}>
                        {formatCurrency(taskSpendingTotalsAdjusted.actualDollars ?? 0)}
                      </td>
                      <td
                        style={{
                          padding: '10px 6px',
                          borderTop: '2px solid #e6e6e6',
                          textAlign: 'right',
                          fontWeight: 800,
                          color: getDeltaVarianceColor({
                            forecastDollars: taskSpendingPastTotalsAdjusted.forecastDollars,
                            deltaDollars: taskSpendingPastTotalsAdjusted.deltaDollars,
                          }),
                        }}
                      >
                        {formatCurrency(taskSpendingPastTotalsAdjusted.deltaDollars ?? 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="ghost"
            onClick={() => void downloadTaskSpendingExcel()}
            disabled={taskSpendingRows.length === 0}
          >
            Download Excel
          </Button>
          <Button
            appearance="ghost"
            onClick={downloadTaskSpendingCsv}
            disabled={taskSpendingRows.length === 0}
          >
            Download CSV
          </Button>
          <Button appearance="primary" onClick={() => setTaskSpendingOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={addTaskOpen}
        onClose={() => {
          setAddTaskOpen(false);
          setNewTaskName('');
          setNewTaskBudgetTarget('');
          setNewTaskType('labor');
          setNewTaskLeadUserId(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Add Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#666' }}>Task name</div>
            <Input value={newTaskName} onChange={(v) => setNewTaskName(v)} placeholder="Enter task name" />

            <div style={{ fontSize: 12, color: '#666' }}>Budget (optional)</div>
            <Input
              value={newTaskBudgetTarget}
              onChange={(v) => {
                const digits = digitsOnly(v);
                if (!digits) {
                  setNewTaskBudgetTarget('');
                  return;
                }
                const n = Number(digits);
                setNewTaskBudgetTarget(Number.isFinite(n) ? formatCurrencyInput(n) : '');
              }}
              placeholder="$25,000"
              style={{ textAlign: 'left' }}
            />

            <div style={{ fontSize: 12, color: '#666' }}>Task type</div>
            <SelectPicker
              cleanable={false}
              searchable={false}
              style={{ width: '100%' }}
              data={TASK_TYPE_OPTIONS}
              value={newTaskType}
              onChange={(v) => setNewTaskType(v === 'odc' ? 'odc' : 'labor')}
              placeholder="Select task type"
            />

            <div style={{ fontSize: 12, color: '#666' }}>Task lead</div>
            <SelectPicker
              cleanable
              searchable
              style={{ width: '100%' }}
              data={projectMemberOptions}
              value={Number.isInteger(newTaskLeadUserId) ? newTaskLeadUserId : null}
              onChange={(v) => setNewTaskLeadUserId(Number.isInteger(v) ? v : null)}
              placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead'}
              disabled={projectUsersLoading || projectMemberOptions.length === 0}
            />

            {createTask.isError ? (
              <Message type="error" showIcon>
                {String(createTask.error?.message || createTask.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" loading={createTask.isPending} disabled={createTask.isPending} onClick={submitAddTask}>
            Create
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              setAddTaskOpen(false);
              setNewTaskName('');
              setNewTaskBudgetTarget('');
              setNewTaskType('labor');
              setNewTaskLeadUserId(null);
            }}
            disabled={createTask.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={addTaskFromWbsOpen}
        onClose={() => {
          if (addTaskFromWbsPending || createTaskFromWbs.isPending) return;
          setAddTaskFromWbsOpen(false);
          setWbsTemplateId(null);
          setWbsTaskName('');
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Add Task from WBS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {wbsTemplatesError ? (
            <Message type="error" showIcon>
              Failed to load WBS templates.
            </Message>
          ) : null}

          {Array.isArray(wbsTemplates) && wbsTemplates.length === 0 ? (
            <Message type="info" showIcon>
              No templates yet. Create one in the WBS Manager.
            </Message>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Task name</div>
              <Input value={wbsTaskName} onChange={(v) => setWbsTaskName(v)} placeholder="Enter task name" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>WBS template</div>
              <SelectPicker
                cleanable={false}
                searchable
                style={{ width: '100%' }}
                placeholder={wbsTemplatesLoading ? 'Loading templates…' : 'Select a template'}
                data={(wbsTemplates || [])
                  .map((t) => ({ label: t?.name || '—', value: Number(t?.id) }))
                  .filter((o) => Number.isInteger(o.value))}
                value={Number.isInteger(Number(wbsTemplateId)) ? Number(wbsTemplateId) : null}
                onChange={(v) => {
                  const next = Number.isInteger(v) ? v : null;
                  setWbsTemplateId(next);
                  if (!String(wbsTaskName || '').trim()) {
                    const found = (wbsTemplates || []).find((t) => Number(t?.id) === Number(next));
                    const name = String(found?.name ?? '').trim();
                    if (name) setWbsTaskName(name);
                  }
                }}
                disabled={wbsTemplatesLoading || (wbsTemplates || []).length === 0 || addTaskFromWbsPending}
              />
            </div>

            {wbsItemsError ? (
              <Message type="error" showIcon>
                Failed to load template items.
              </Message>
            ) : null}

            {Number.isInteger(Number(wbsTemplateId)) ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Activities to add</div>
                <div
                  style={{
                    border: '1px solid #eee',
                    borderRadius: 10,
                    padding: 10,
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  {wbsItemsLoading ? <div style={{ color: '#666' }}>Loading…</div> : null}
                  {!wbsItemsLoading && (wbsItems || []).length === 0 ? (
                    <div style={{ color: '#666', fontSize: 12 }}>No items in this template.</div>
                  ) : null}

                  {!wbsItemsLoading && sortedWbsItems.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {wbsSelectedItemIds.length} / {sortedWbsItems.length} selected
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button
                            size="xs"
                            appearance="subtle"
                            onClick={() => setWbsSelectedItemIds(sortedWbsItems.map((i) => i.id))}
                            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                          >
                            Select all
                          </Button>
                          <Button
                            size="xs"
                            appearance="subtle"
                            onClick={() => setWbsSelectedItemIds([])}
                            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {sortedWbsItems.map((i, idx) => {
                          const checked = wbsSelectedItemIds.includes(i.id);
                          return (
                            <div
                              key={i.id}
                              style={{
                                padding: '10px 8px',
                                borderBottom: idx === sortedWbsItems.length - 1 ? 'none' : '1px solid #f0f0f0',
                              }}
                            >
                              <Checkbox
                                checked={checked}
                                onChange={(_value, nextChecked) => {
                                  setWbsSelectedItemIds((prev) => {
                                    const base = Array.isArray(prev) ? prev : [];
                                    if (nextChecked) return Array.from(new Set([...base, i.id]));
                                    return base.filter((x) => Number(x) !== i.id);
                                  });
                                }}
                                disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <div style={{ fontWeight: 700, lineHeight: '16px' }}>{i.title}</div>
                                  {i.assumptions ? (
                                    <div style={{ fontSize: 12, color: '#666', lineHeight: '16px' }}>{i.assumptions}</div>
                                  ) : null}
                                </div>
                              </Checkbox>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {createTaskFromWbs.isError ? (
              <Message type="error" showIcon>
                {String(createTaskFromWbs.error?.message || createTaskFromWbs.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={submitAddTaskFromWbs}
            loading={addTaskFromWbsPending || createTaskFromWbs.isPending}
            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
          >
            Create
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              if (addTaskFromWbsPending || createTaskFromWbs.isPending) return;
              setAddTaskFromWbsOpen(false);
              setWbsTemplateId(null);
              setWbsTaskName('');
            }}
            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
